/**
 * YouTube proxy player: wraps backend HTTPS iframe (server/src/middlewares/youtubeProxy)
 * to solve Cordova iOS protocol mismatch (ionic://localhost)
 */

import React, { useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { getApiOrigin } from "../tools/env";
import { useStandardYouTubeEmbed } from "../hooks/useStandardYouTubeEmbed";
import { useDBLog } from "hooks/useDBLog";

// Type definitions for the YouTube proxy protocol
export interface YouTubeProxyPlayerAPI {
  playVideo: () => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  seekTo: (time: number, allowSeekAhead: boolean) => void;
  setPlaybackRate: (rate: number) => void;
  getCurrentTime: () => number;
  getPlaybackRate: () => number;
  getPlayerState: () => number;
}

export interface YouTubeProxyPlayerProps {
  videoId: string;
  startTime?: number;
  width: number;
  height: number;
  className?: string;
  onReady?: () => void;
  onStateChange?: (state: number) => void;
  onEnd?: () => void;
}

export const YouTubeProxyPlayer = forwardRef<YouTubeProxyPlayerAPI, YouTubeProxyPlayerProps>(
  ({ videoId, startTime = 0, width, height, className, onReady, onStateChange, onEnd }, ref) => {
    const dblog = useDBLog();
    const [useStandardEmbed, setUseStandardEmbed] = React.useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const currentTimeRef = useRef<number>(0);
    const playbackRateRef = useRef<number>(1);
    const playerStateRef = useRef<number>(-1); // -1: unstarted, 0: ended, 1: playing, 2: paused, 3: buffering, 5: video cued

    // Store callbacks in refs to avoid re-running useEffect when they change
    const callbacksRef = useRef({ onReady, onStateChange, onEnd });
    useEffect(() => {
      callbacksRef.current = { onReady, onStateChange, onEnd };
    }, [onReady, onStateChange, onEnd]);

    const playerRef = useStandardYouTubeEmbed({
      enabled: useStandardEmbed,
      iframeRef,
      videoId,
      onReady,
      onStateChange,
      onEnd,
      currentTimeRef,
      playbackRateRef,
      playerStateRef
    });

    // Unified API methods that check dynamically on each call
    useImperativeHandle(
      ref,
      () => {
        const isDirect = () => useStandardEmbed && playerRef.current;
        const sendCmd = (cmd: string, args?: any[]) =>
          iframeRef.current?.contentWindow?.postMessage({ command: cmd, args }, "*");

        return {
          playVideo: () => (isDirect() ? playerRef.current.playVideo() : sendCmd("playVideo")),
          pauseVideo: () => (isDirect() ? playerRef.current.pauseVideo() : sendCmd("pauseVideo")),
          stopVideo: () => (isDirect() ? playerRef.current.stopVideo() : sendCmd("stopVideo")),
          seekTo: (time: number, allowSeekAhead: boolean) =>
            isDirect() ? playerRef.current.seekTo(time, allowSeekAhead) : sendCmd("seekTo", [time, allowSeekAhead]),
          setPlaybackRate: (rate: number) =>
            isDirect() ? playerRef.current.setPlaybackRate(rate) : sendCmd("setPlaybackRate", [rate]),
          getCurrentTime: () => (isDirect() ? playerRef.current.getCurrentTime() ?? 0 : currentTimeRef.current),
          getPlaybackRate: () => (isDirect() ? playerRef.current.getPlaybackRate() ?? 1 : playbackRateRef.current),
          getPlayerState: () => (isDirect() ? playerRef.current.getPlayerState() ?? -1 : playerStateRef.current)
        };
      },
      [useStandardEmbed, playerRef]
    );

    // Handle messages from proxy iframe
    useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
        if (!event.data?.type?.startsWith("youtube-")) return;
        const { type, data } = event.data;

        switch (type) {
          case "youtube-ready":
            callbacksRef.current.onReady?.();
            break;
          case "youtube-stateChange":
            currentTimeRef.current = data.currentTime;
            playerStateRef.current = data.state;
            callbacksRef.current.onStateChange?.(data.state);
            break;
          case "youtube-timeUpdate":
            currentTimeRef.current = data.currentTime;
            break;
          case "youtube-playbackRateChange":
            playbackRateRef.current = data.rate;
            break;
          case "youtube-end":
            callbacksRef.current.onEnd?.();
            break;
          case "youtube-error":
            dblog("youtube-error", "Fallback to standard embed on error", {
              code: data.code,
              message: data.message,
              videoId
            });
            setUseStandardEmbed(true);
            break;
        }
      };

      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }, [videoId, dblog]);

    const iframeSrc = useStandardEmbed
      ? `https://www.youtube.com/embed/${videoId}?start=${startTime}&enablejsapi=1`
      : `${getApiOrigin()}/youtube-proxy?videoId=${videoId}&start=${startTime}`;

    return (
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="YouTube video player"
        width={width}
        height={height}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
        style={{ border: 0 }}
        className={className}
      />
    );
  }
);

YouTubeProxyPlayer.displayName = "YouTubeProxyPlayer";
